<template>
  <ul class="flex gap-x-2.5">
    <li
      v-for="item in mappedStatuses"
      :key="item.id"
      class="last:mr-0"
      :class="[
        isCanceledStatus(item) ? 'max-w-full flex-1' : 'max-w-[25%] flex-[25%]',
      ]"
    >
      <div
        class="mb-1.5 h-1"
        :class="[
          currentStatus?.order_status === item.order_status ||
          item.position < (currentStatus?.position || 0)
            ? statusColor(item)
            : 'bg-gray-bg',
        ]"
      />
      <div
        class="text-xs font-light text-gray-disabled"
        :class="[
          currentStatus?.order_status === item.order_status ||
          item.position < (currentStatus?.position || 0)
            ? ''
            : 'text-gray-strokes',
        ]"
      >
        <p class="hidden" :class="{ 'opacity-0': item.date_to_show === '-' }">
          {{ item.date_to_show }}
        </p>
        <p>{{ item.sub_text }}</p>
      </div>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { format } from "date-fns";
import {
  OrderStatus,
  orderStatusesOptions,
  OrderData,
  statusesOptions,
} from "ecom-types";

const props = defineProps<{
  status: number;
  items: OrderStatus[];
  order: OrderData;
}>();

const currentStatus = computed(() => {
  return props.items.find((el: any) => el.order_status === props.status);
});

const statuses = computed<OrderStatus[]>(() =>
  [...props.items].sort(
    (a: OrderStatus, b: OrderStatus) => a.position - b.position,
  ),
);

const mappedStatuses = computed(() => {
  return statuses.value.map((el) => {
    switch (el.order_status) {
      case orderStatusesOptions.ORDER_PLACED: {
        return {
          ...el,
          date_to_show: formateDate(props.order.customer_date),
        };
      }
      case orderStatusesOptions.IN_PRODUCTION: {
        if (el.status === statusesOptions.IN_PROGRESS) {
          return {
            ...el,
            date_to_show: formateDate(el.started_at),
          };
        } else if (el.status === statusesOptions.COMPLETED) {
          return {
            ...el,
            date_to_show: formateDate(el.completed_at),
          };
        } else {
          return {
            ...el,
            date_to_show: "-",
          };
        }
      }
      case orderStatusesOptions.IN_TRANSIT: {
        const inProgressStatus = statuses.value.find(
          (i) => i.order_status === orderStatusesOptions.IN_PRODUCTION,
        );
        if (
          inProgressStatus?.status === statusesOptions.IN_PROGRESS ||
          (inProgressStatus?.status === statusesOptions.COMPLETED && !el.status)
        ) {
          return {
            ...el,
            date_to_show: `-`,
          };
        } else if (el.status === statusesOptions.IN_PROGRESS) {
          return {
            ...el,
            date_to_show: formateDate(el.started_at),
          };
        } else if (el.status === statusesOptions.COMPLETED) {
          return {
            ...el,
            date_to_show: formateDate(el.completed_at),
          };
        } else {
          return {
            ...el,
            date_to_show: "-",
          };
        }
      }
      case orderStatusesOptions.DELIVERED: {
        const inTransitStatus = statuses.value.find(
          (i) => i.order_status === orderStatusesOptions.IN_TRANSIT,
        );
        if (inTransitStatus?.status === statusesOptions.IN_PROGRESS) {
          return {
            ...el,
            date_to_show: `-`,
          };
        } else {
          return {
            ...el,
            date_to_show:
              el.status === statusesOptions.COMPLETED
                ? formateDate(props.order.ship_date)
                : `-`,
          };
        }
      }
      case orderStatusesOptions.CANCELED: {
        return {
          ...el,
          date_to_show: formateDate(el.completed_at),
        };
      }
      default: {
        return {
          ...el,
          date_to_show: `-`,
        };
      }
    }
  });
});

function statusColor(item: OrderStatus) {
  return isCanceledStatus(item) ? "bg-error-text" : "bg-green-bg";
}
function isCanceledStatus(item: OrderStatus) {
  return item.order_status === orderStatusesOptions.CANCELED;
}

function formateDate(date: string) {
  return format(new Date(date), "MM/dd/yyyy");
}
</script>
